import { inject, observer } from "mobx-react";
import React, { Component } from "react";
import AnimateHeight from "react-animate-height";
import { tx } from "src/libs/i18n";
import { IFormSelect } from "src/models";
import { LibraryStore } from "src/stores";
import {
  Button,
  Checkbox,
  DynamicImage,
  Input,
  Modal,
  P,
} from "src/views/shared/components";
import * as styles from "./AddDocumentToLibraryModal.module.scss";

export interface AddDocumentToLibraryModalProps {
  libraryStore?: LibraryStore;
}

@inject("libraryStore")
@observer
export class AddDocumentToLibraryModal extends Component<
  AddDocumentToLibraryModalProps
> {
  renderCheckbox = (post: IFormSelect) => (
    <div key={post.id} className={styles.checkbox}>
      <Checkbox
        type="check"
        selected={post.selected}
        onChange={selected => (post.selected = selected)}
      />
      <P className={styles.checkboxDescription}>{post.title}</P>
    </div>
  );

  renderForm = () => {
    const {
      createCollection,
      collections,
      validate,
      createCollectionformData: formData,
    } = this.props.libraryStore;

    if (!formData) {
      return null;
    }

    return (
      <>
        {collections.map(this.renderCheckbox)}

        <div className={styles.checkbox}>
          <Checkbox
            type="check"
            selected={createCollection}
            onChange={selected =>
              (this.props.libraryStore.createCollection = selected)
            }
          />
          <P className={styles.checkboxDescriptionSecondary}>
            {tx("AddDocumentToLibraryModal.createCollection")}
          </P>
        </div>

        <AnimateHeight
          height={createCollection ? "auto" : 0}
          className={styles.inputSection}
        >
          <Input
            className={styles.input}
            type="text"
            label={tx("misc.collectionTitle")}
            placeholder={tx("misc.collectionTitle")}
            htmlFor="upload_collection_title"
            value={formData.title.value}
            onChange={value => (formData.title.value = value)}
            onBlur={() => validate(formData.title)}
            error={formData.title.valid === false}
          />

          <Input
            className={styles.input}
            type="textarea"
            label={tx("misc.description")}
            placeholder={tx("misc.description")}
            htmlFor="upload_collection_description"
            value={formData.description.value}
            onChange={value => (formData.description.value = value)}
            onBlur={() => validate(formData.description)}
            error={formData.description.valid === false}
          />
        </AnimateHeight>
      </>
    );
  };

  renderImage = () => {
    const { document } = this.props.libraryStore;

    let image = null;

    switch (document.nodeType) {
      case "Document":
        image = document.documentThumbnail?.image;
        break;

      case "Article":
        image = document.articleFields?.imageGroup;
        break;
    }

    return (
      <DynamicImage
        image={image}
        size="mediumLarge"
        className={styles.coverImage}
        alt={"card-image"}
      />
    );
  };

  render() {
    const {
      isOpen,
      document,
      onCloseUpdateModal,
      onAddToLibrary,
      collections,
      addToLibraryResponse,
    } = this.props.libraryStore;

    if (!document) {
      return null;
    }

    return (
      <Modal
        title={tx("AddDocumentToLibraryModal.addToLibrary")}
        open={isOpen}
        onClose={onCloseUpdateModal}
        className={styles.wrapper}
      >
        <div className={styles.contentWrapper}>
          {this.renderImage()}

          <h2 className={styles.title}>{document.title}</h2>

          {this.renderForm()}

          {addToLibraryResponse?.message?.length > 0 && (
            <>
              <P
                className={`${styles.resultText} ${
                  addToLibraryResponse.error ? styles.error : styles.success
                }`}
                medium
              >
                {addToLibraryResponse.message}
              </P>
              {!addToLibraryResponse.error && (
                <P medium>{tx("saveToLibrary.closingShortly")}</P>
              )}
            </>
          )}

          <Button
            className={styles.addButton}
            size="large"
            color="red"
            onClick={onAddToLibrary}
          >
            {collections?.find(col => col.selected)
              ? tx("AddDocumentToLibraryModal.addToCollection")
              : tx("AddDocumentToLibraryModal.addToLibrary")}
          </Button>
        </div>
      </Modal>
    );
  }
}
